import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CalendarCheck2, HeartHandshake, ShieldCheck, Stethoscope, UserCheck, Users } from 'lucide-react';
import { scrollToTop } from '../utils/scrollUtils';

const serviceTypes = [
  {
    icon: Stethoscope,
    title: 'Medical transport support',
    description:
      'Plan journeys for hospitals, GP appointments, outpatient care, rehabilitation, and repeat treatment schedules.',
  },
  {
    icon: HeartHandshake,
    title: 'Assisted everyday travel',
    description:
      'Arrange practical transport for shopping, social visits, day centres, and routine commitments with extra help.',
  },
  {
    icon: Users,
    title: 'Companion-supported journeys',
    description:
      'Include a trained companion where extra reassurance or physical assistance is needed throughout the trip.',
  },
];

const audiences = [
  'People with physical disabilities or reduced mobility',
  'Passengers living with chronic illness or long-term conditions',
  'Older adults who need steady, assisted travel support',
  'Families, carers, and care coordinators booking on someone else’s behalf',
];

const differentiators = [
  {
    icon: UserCheck,
    title: 'Personalised assistance',
    description: 'Journeys are tailored around mobility, health, timing, and communication needs.',
  },
  {
    icon: CalendarCheck2,
    title: 'Intelligent matching',
    description: 'AbleGo matches requests to suitable vehicles and trained support to reduce failed journeys.',
  },
  {
    icon: ShieldCheck,
    title: 'Reliable continuity',
    description: 'Recurring journey planning helps maintain routine, treatment attendance, and peace of mind.',
  },
];

const faqs = [
  {
    question: 'What disabled transport services does AbleGo provide?',
    answer:
      'AbleGo provides pre-bookable disabled transport services for medical journeys, daily assisted travel, and companion-supported trips across the UK.',
  },
  {
    question: 'Can I set up recurring disabled transport services?',
    answer:
      'Yes. You can arrange recurring bookings for regular appointments, treatment plans, education, work, or social routines.',
  },
  {
    question: 'Who can book disabled transport services with AbleGo?',
    answer:
      'Passengers can book directly, and family members, carers, support workers, and care coordinators can also arrange journeys on behalf of someone else.',
  },
  {
    question: 'Do your disabled transport services include companion support?',
    answer:
      'Yes. You can request companion-supported journeys where additional practical support is needed from pickup to destination.',
  },
  {
    question: 'How do you keep journeys safe and reliable?',
    answer:
      'AbleGo uses vetted drivers, accessibility-aware service standards, and pre-booked scheduling to improve reliability and passenger safety.',
  },
  {
    question: 'Are disabled transport services only for wheelchair users?',
    answer:
      'No. AbleGo supports a wide range of mobility, disability, and health conditions, including passengers who do not use wheelchairs but still need assisted travel.',
  },
];

const DisabledTransportServicesPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Disabled Transport Services UK | Assisted Travel';
    scrollToTop('instant');
  }, []);

  return (
    <>
      <Helmet>
        <title>Disabled Transport Services UK | Assisted Travel</title>
        <meta
          name="description"
          content="Arrange disabled transport services with assisted travel, trained companions and reliable UK coverage. Book planned journeys with AbleGo."
        />
        <link rel="canonical" href="https://ablego.co.uk/disabled-transport-services" />
      </Helmet>

      <div className="pt-20">
        <section className="bg-gradient-to-br from-indigo-700 via-blue-700 to-teal-700 text-white py-16 lg:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl">
              <p className="text-indigo-100 uppercase tracking-wide font-semibold mb-4">Planned and supported journeys</p>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                Disabled Transport Services for Ongoing Support
              </h1>
              <p className="text-lg lg:text-xl text-indigo-50 max-w-3xl mb-8">
                AbleGo helps you arrange disabled transport services with the right level of support, whether you need one trip or recurring assisted travel.
              </p>
              <Link
                to="/booking"
                className="inline-flex items-center px-7 py-4 rounded-xl bg-white text-indigo-700 font-semibold hover:bg-indigo-50 transition-colors"
              >
                Arrange Transport
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Service types available</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {serviceTypes.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="p-6 rounded-2xl bg-gray-50 dark:bg-dark-800 border border-gray-100 dark:border-dark-700">
                    <Icon className="w-7 h-7 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-3">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-dark-800">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Who disabled transport services are for</h2>
            <ul className="space-y-4">
              {audiences.map((item) => (
                <li key={item} className="flex items-start gap-3 p-5 rounded-xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                  <ShieldCheck className="w-6 h-6 text-teal-600 mt-0.5" />
                  <p className="text-gray-700 dark:text-gray-200">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-14 bg-blue-600 text-white">
          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-3xl font-bold mb-3">Need support beyond a one-off journey?</h2>
                <p className="text-blue-100">
                  AbleGo is designed for ongoing travel needs, not just single ad-hoc trips.
                </p>
              </div>
              <Link
                to="/booking"
                className="inline-flex justify-center items-center px-7 py-4 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors"
              >
                Book a Ride
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Why families and carers choose AbleGo</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {differentiators.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="rounded-2xl border border-gray-200 dark:border-dark-700 p-6">
                    <Icon className="w-7 h-7 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-3">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-dark-800">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Safety and trust</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <article className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                <ShieldCheck className="w-7 h-7 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Vetted teams</h3>
                <p className="text-gray-600 dark:text-gray-300">Driver and support profiles are reviewed to maintain service quality and safety standards.</p>
              </article>
              <article className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                <Users className="w-7 h-7 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Assistance-led journeys</h3>
                <p className="text-gray-600 dark:text-gray-300">Support is built around passenger needs, from entry and exit help to calm journey handling.</p>
              </article>
              <article className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                <CalendarCheck2 className="w-7 h-7 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Consistent reliability</h3>
                <p className="text-gray-600 dark:text-gray-300">Pre-planned schedules and clear journey information support better punctuality.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Frequently asked questions</h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <details key={faq.question} className="group rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800 p-5">
                  <summary className="cursor-pointer list-none font-semibold text-gray-900 dark:text-gray-50 flex justify-between items-center">
                    {faq.question}
                    <span className="ml-4 text-indigo-600 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-900 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Set up reliable support for future journeys</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Start with one booking or plan recurring transport based on your long-term needs.
            </p>
            <Link
              to="/booking"
              className="inline-flex items-center px-8 py-4 rounded-xl bg-teal-500 text-white font-semibold hover:bg-teal-400 transition-colors"
            >
              Get Started
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default DisabledTransportServicesPage;
